/* eslint-disable react-hooks/exhaustive-deps */
import React, { RefObject, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isEqual, sortBy } from 'lodash';
import { getCellRef } from 'components/FeaturedSpreadsheet/utilities/utilities';
import FeaturedSpreadsheetContext, { FeaturedSpreadsheetContextType } from '../../context/FeaturedSpreadsheetContext';

const DRAGGING_COLUMN = 'dragging-column';
const DRAGGING_COLUMN_CONTAINER = 'dragging-column-container';
const DRAGGED_CELL = 'dragged-cell';
const LTR_LEFT = 'ltr-left';
const LTR_RIGHT = 'ltr-right';
const RTL_LEFT = 'rtl-left';
const RTL_RIGHT = 'rtl-right';

interface DraggableColumnsProps {
  tableRef: RefObject<HTMLElement>;
  children: React.ReactElement;
}

const DraggableColumns: React.FC<DraggableColumnsProps> = ({ tableRef, children }) => {
  const {
    cells,
    columns,
    setColumns,
    allowReorderColumns,
    reverseParser,
    disabled,
    pageFieldAttributes,
    selectedCell,
  } = useContext<FeaturedSpreadsheetContextType>(FeaturedSpreadsheetContext);

  const [draggingCell, setDraggingCell] = useState<HTMLElement | null>(null);
  const [draggingCells, setDraggingCells] = useState<HTMLElement[] | null>(null);
  const [draggingOverCell, setDraggingOverCell] = useState<HTMLElement | null>(null);

  const table = useMemo(() => {
    if (tableRef.current) {
      return document.querySelector('#spreadsheet-table__content-columns table');
    }
    return null;
  }, [tableRef.current]);

  const draggableCells = useMemo(() => {
    if (tableRef.current) {
      return document.querySelectorAll('#spreadsheet-table__content-columns table > tbody > tr > td:not(.legend)');
    }
    return null;
  }, [table, columns]);

  const draggableButtons = useMemo(() => {
    if (!disabled && tableRef.current) {
      return document.querySelectorAll('#spreadsheet-table__content-columns .column-rearrange');
    }
    return null;
  }, [table, disabled, columns]);

  const setCustomPreview = (e: DragEvent, draggingCell: HTMLElement): void => {
    const previewTable = table?.cloneNode(false) as HTMLTableElement;
    if (!previewTable) {
      return;
    }

    // Clone the table without its childrens
    const newTbody = document.createElement('tbody');
    const newRow = document.createElement('tr');

    // Add some new styles to the table
    previewTable.classList.add(DRAGGING_COLUMN);
    newTbody.appendChild(newRow);
    previewTable.appendChild(newTbody);

    // Filter all the cells outside the column dragged
    const tempDraggingCells = [
      ...document.querySelectorAll(`tr.visible > td[data-column-ref="${draggingCell.dataset.columnRef}"]:not(.legend)`),
    ] as HTMLElement[];
    setDraggingCells(tempDraggingCells);

    tempDraggingCells.forEach(cell => {
      // Insert cells in the new filtered table
      const newCell = cell.cloneNode(true) as HTMLElement;
      newCell.style.display = 'block';
      previewTable.querySelector('tr')?.appendChild(newCell);

      // Adding styles to the originall cells while draging
      cell.classList.add(DRAGGED_CELL);
    });

    // Set the custom preview
    const previewContainer = document.createElement('div');
    previewContainer.classList.add(DRAGGING_COLUMN_CONTAINER);
    previewContainer.appendChild(previewTable);

    document.body.appendChild(previewContainer);
    e.dataTransfer?.setDragImage(previewTable, 0, 0);
  };

  const getDropSide = (e: MouseEvent): string | null => {
    if (!draggingOverCell) {
      return null;
    }
    if (draggingCell) {
      const startingPosition = draggingCell.getBoundingClientRect().left;
      const hoveredCell = draggingOverCell.getBoundingClientRect();
      const mousePosition = e.clientX;
      const cellsCenter = hoveredCell.left + hoveredCell.width / 2;

      // Dragging from left to right
      if (startingPosition < mousePosition) {
        return mousePosition < cellsCenter ? LTR_LEFT : LTR_RIGHT;
      }

      // Dragging from right to left
      if (startingPosition > mousePosition) {
        return mousePosition > cellsCenter ? RTL_RIGHT : RTL_LEFT;
      }
    }
    return null;
  };

  const showDropIndicator = (e: MouseEvent): void => {
    const dropSide = getDropSide(e);
    const hoveredCell = (e.target as HTMLElement).closest('tr.visible > td') as HTMLElement;
    setDraggingOverCell(hoveredCell);

    if (dropSide) {
      // Get the column legend of the dragged over cell
      const hoveredCellColumnLegend = hoveredCell.dataset.columnLegend ?? '';

      const hoveredColumnCells = [
        ...document.querySelectorAll(`tr > td[data-column-legend="${hoveredCellColumnLegend}"]`),
      ] as HTMLElement[];

      // Filter the draggable cells by this column legend
      hoveredColumnCells.forEach(cell => {
        cell.classList.remove(LTR_LEFT, LTR_RIGHT, RTL_LEFT, RTL_RIGHT);
        cell.classList.add(dropSide);
      });
    }
  };

  const hideDropIndicator = (): void => {
    if (draggableCells) {
      draggableCells.forEach(cell => {
        (cell as HTMLElement).classList.remove(LTR_LEFT, LTR_RIGHT, RTL_LEFT, RTL_RIGHT);
      });
    }
  };

  const cleanDraggingCellsStyles = (): void => {
    const tmpDraggingCells = Array.isArray(draggingCells) ? [...draggingCells] : [];
    tmpDraggingCells.forEach(cell => cell.classList.remove(DRAGGED_CELL));
  };

  const rearrangeColumns = (e: MouseEvent): void => {
    const originColumnRef = draggingCell?.dataset?.columnRef ?? '';
    const destinationColumnRef = draggingOverCell?.dataset.columnRef ?? '';
    if (reverseParser) {
      const updatedColumns = sortBy(reverseParser(cells, columns, pageFieldAttributes), ['order']);
      const originColumnIndex = updatedColumns.findIndex(
        column => column.columnRef.toString() === originColumnRef.toString()
      );

      if (originColumnIndex === -1) {
        return;
      }

      const destinationColumnIndex = updatedColumns.findIndex(
        column => column.columnRef.toString() === destinationColumnRef.toString()
      );
  
      // Cut the origin column from the updated list
      const originColumn = { ...updatedColumns[originColumnIndex] };
  
      updatedColumns.splice(originColumnIndex, 1);
  
      // Paste the origin column in the updated list using the new position
      switch (getDropSide(e)) {
        case LTR_LEFT:
          updatedColumns.splice(destinationColumnIndex - 1, 0, originColumn);
          break;
        case RTL_RIGHT:
          updatedColumns.splice(destinationColumnIndex + 1, 0, originColumn);
          break;
        default:
          updatedColumns.splice(destinationColumnIndex, 0, originColumn);
          break;
      }
  
      // Change only if the order is different
      const oldOrder = columns.map(column => column.order);
      const newOrder = updatedColumns.map(column => column.order);
      const hasOrder = columns.reduce((a, b) => Number(a) + Number(b.order ?? 0), 0) > 0;
  
      if (isEqual(oldOrder, newOrder) && hasOrder) {
        cleanDraggingCellsStyles();
      } else {
        // Update the order prop
        const sortedColumns = updatedColumns.map((column, columnIndex) => ({
          ...column,
          order: columnIndex,
        }));
        if (setColumns) {
          setColumns(sortedColumns);
        }
      }
    }
  };

  const draggStartHandler = (event: DragEvent): void => {
    if (!selectedCell) {
      return;
    }
    
    const { cellInfo } = selectedCell;
    if (!cellInfo.key) {
      return;
    }
    const tempDraggingCell = getCellRef(tableRef, cellInfo.key) as HTMLElement;
    setDraggingCell(tempDraggingCell);
    setCustomPreview(event, tempDraggingCell);
  };

  const draggOverHandler = (e: DragEvent): void => {
    e.preventDefault();
    hideDropIndicator();
    showDropIndicator(e);
  };

  const draggLeaveHandler = (): void => {
    hideDropIndicator();
  };

  const draggEndHandler = (e: DragEvent): void => {
    hideDropIndicator();
    const container = document.querySelector('.dragging-column-container');
    if (container) {
      document.body.removeChild(container);
    }
    if (draggingOverCell) {
      rearrangeColumns(e);
    }
  };

  useEffect(() => {
    if (columns?.length && draggingCells) {
      cleanDraggingCellsStyles();
    }
  }, [columns]);

  const addEvents = (): void => {
    if (!draggableButtons) {
      return;
    }
    
    draggableButtons.forEach(button => {
      const buttonElement = button as HTMLElement;
      buttonElement.setAttribute('draggable', 'true');
      buttonElement.addEventListener('dragstart', draggStartHandler as EventListener);
      buttonElement.addEventListener('dragover', (e: Event) => {
        e.preventDefault();
        const dragEvent = e as DragEvent;
        const hoveredCell = (dragEvent.target as HTMLElement).closest('tr.visible > td') as HTMLElement;
        setDraggingOverCell(hoveredCell);
      });
      buttonElement.addEventListener('dragend', draggEndHandler as EventListener);
    });

    if (!draggableCells) {
      return;
    }

    draggableCells.forEach(cell => {
      const cellElement = cell as HTMLElement;
      cellElement.setAttribute('draggable', 'true');
      cellElement.addEventListener('dragstart', (e: Event) => e.preventDefault());
      cellElement.addEventListener('dragover', draggOverHandler as EventListener);
      cellElement.addEventListener('dragleave', draggLeaveHandler as EventListener);
    });
  };

  const removeEvents = (): void => {
    if (!draggableButtons) {
      return;
    }
    
    draggableButtons.forEach(button => {
      const buttonElement = button as HTMLElement;
      buttonElement.setAttribute('draggable', 'false');
      buttonElement.removeEventListener('dragstart', draggStartHandler as EventListener);
      buttonElement.removeEventListener('dragend', draggEndHandler as EventListener);
    });

    if (!draggableCells) {
      return;
    }

    draggableCells.forEach(cell => {
      const cellElement = cell as HTMLElement;
      cellElement.setAttribute('draggable', 'false');
      cellElement.removeEventListener('dragover', draggOverHandler as EventListener);
      cellElement.removeEventListener('dragleave', draggLeaveHandler as EventListener);
    });
  };

  useEffect(() => {
    if (selectedCell && allowReorderColumns && !disabled && !isEmpty(draggableCells) && !isEmpty(draggableButtons)) {
      addEvents();
    }
    return () => {
      if (!isEmpty(draggableButtons)) {
        removeEvents();
      }
    };
  }, [columns, draggableCells, draggingCell, draggingOverCell, disabled, table, draggableButtons, selectedCell]);

  return <>{children}</>;
};

export default DraggableColumns;